import {
  BuildingIcon,
  DashboardIcon,
  DesktopIcon,
  ClockIcon,
  AccessIcon,
  LinkIcon,
  Sidebar as BpSidebar,
} from "@bp/ui-components";
import type { SidebarContentProps } from "@bp/ui-components/dist/@bp/lib/Sidebar/Sidebar";
import classNames from "classnames";
import { type ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppLayout } from "contexts/AppLayoutContext";
import { getNavGroups } from "./nav";
import styles from "./Sidebar.module.scss";

// Übersicht uses planung's DashboardIcon (its "home"); the rest keep our icons.
const NAV_ICON: Record<string, ReactNode> = {
  overview: <DashboardIcon />,
  orgs: <BuildingIcon />,
  apps: <DesktopIcon />,
  clients: <AccessIcon />,
  federated: <LinkIcon />,
  audit: <ClockIcon />,
};

/**
 * App sidebar — the DS `Sidebar` used exactly as planung-frontend does: expand
 * on hover, pin via the footer button, `isExpanded`/`isFixed` held in the
 * layout context. Keeps our nav groups + icons + the schulsuite-support logo.
 */
export function Sidebar() {
  const { sidebarIsOpen, setSidebarIsOpen, sidebarIsFixed, setSidebarIsFixed } = useAppLayout();
  const location = useLocation();

  // planung parity: nudge viewport listeners once the pin animation settles.
  const [pinState, setPinState] = useState(sidebarIsFixed);
  useEffect(() => {
    if (sidebarIsFixed !== pinState) {
      setPinState(sidebarIsFixed);
      const timer = setTimeout(() => window.dispatchEvent(new Event("resize")), 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [sidebarIsFixed, pinState]);

  const sidebarContent: SidebarContentProps[] = getNavGroups().map((group) => ({
    title: group.title,
    items: group.items.map((item) => ({
      // DS matches currentLocationPath against relative links (no leading slash)
      link: item.to === "/" ? "" : item.to.replace(/^\//, ""),
      title: item.label,
      icon: NAV_ICON[item.id],
    })),
  }));

  return (
    <BpSidebar
      isDesktop
      className={classNames(styles.sidebar, "has-scrollbar")}
      currentLocationPath={location.pathname}
      isExpanded={sidebarIsOpen}
      setIsExpanded={setSidebarIsOpen}
      isFixed={sidebarIsFixed}
      setIsFixed={setSidebarIsFixed}
      logoType="schulsuite-support"
      sidebarContent={sidebarContent}
    />
  );
}
